export interface Size {
  width: number;
  height: number;
}

export interface Rect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface Point {
  x: number;
  y: number;
}

/**
 * CSS size for the canvas so the remote viewport (`device`) fits inside `container` while keeping
 * its aspect ratio. Never upscales beyond `maxScale` (1 = native CSS pixels).
 */
export function fitSize(container: Size, device: Size, maxScale = 1): Size {
  if (container.width <= 0 || container.height <= 0 || device.width <= 0 || device.height <= 0)
    return { width: 0, height: 0 };
  const scale = Math.min(
    container.width / device.width,
    container.height / device.height,
    maxScale,
  );
  return { width: Math.floor(device.width * scale), height: Math.floor(device.height * scale) };
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

/**
 * Maps a client-space point (from a pointer event) to remote CSS pixels using the canvas rect and
 * the frame's `deviceWidth/deviceHeight`. The image itself may be downscaled by the node; that is
 * irrelevant here because the canvas is always laid out in *device* proportions.
 */
export function mapToDevice(client: Point, rect: Rect, device: Size): Point {
  if (rect.width <= 0 || rect.height <= 0) return { x: 0, y: 0 };
  const x = ((client.x - rect.left) * device.width) / rect.width;
  const y = ((client.y - rect.top) * device.height) / rect.height;
  return {
    x: Math.round(clamp(x, 0, device.width) * 100) / 100,
    y: Math.round(clamp(y, 0, device.height) * 100) / 100,
  };
}
